import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../services/api';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import MetricsCard from '../../components/Shared/MetricsCard';
import DataTable from '../../components/Shared/DataTable';
import { superAdminNavItems } from '../../constants/navigation';

/**
 * SuperAdminDashboard - Platform-wide dashboard for super administrators
 * Shows platform metrics, school management, and system-wide analytics
 * Allows creating schools and master activation codes
 */
const SuperAdminDashboard = () => {
  const { currentUser } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateSchoolModal, setShowCreateSchoolModal] = useState(false);
  const [schoolFormData, setSchoolFormData] = useState({
    schoolName: '',
    districtName: '',
    city: '',
    state: '',
    licenseCount: 100,
    subscriptionMonths: 12
  });
  const [createdSchool, setCreatedSchool] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const [dashData, schoolsData] = await Promise.all([
          apiRequest('/api/super-admin/dashboard', 'GET'),
          apiRequest(`/api/super-admin/schools?page=${currentPage}&search=${searchQuery}`, 'GET')
        ]);
        setDashboardData(dashData);
        setSchools(schoolsData.schools || []);
      } catch (err) {
        console.error('Failed to load dashboard:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [currentPage, searchQuery]);

  // Create new school with master code
  const handleCreateSchool = async () => {
    if (!schoolFormData.schoolName) {
      alert('Please enter school name');
      return;
    }

    setCreateLoading(true);
    try {
      const response = await apiRequest('/api/super-admin/schools/create', 'POST', {
        school_name: schoolFormData.schoolName,
        district_name: schoolFormData.districtName,
        city: schoolFormData.city,
        state: schoolFormData.state,
        license_count: parseInt(schoolFormData.licenseCount),
        subscription_months: parseInt(schoolFormData.subscriptionMonths)
      });
      setCreatedSchool(response);
      // Refresh schools list
      const schoolsData = await apiRequest(`/api/super-admin/schools?page=${currentPage}`, 'GET');
      setSchools(schoolsData.schools || []);
    } catch (err) {
      alert(`Failed to create school: ${err.message}`);
    } finally {
      setCreateLoading(false);
    }
  };

  // School table columns
  const schoolColumns = [
    {
      key: 'school_name',
      label: 'School',
      sortable: true,
      render: (school) => (
        <div>
          <p className="font-medium text-gray-900">{school.school_name}</p>
          <p className="text-xs text-gray-500">{school.district_name || 'No district'}</p>
        </div>
      )
    },
    {
      key: 'students',
      label: 'Students',
      sortable: true,
      render: (school) => (
        <div>
          <p className="font-medium text-gray-900">{school.student_count || 0}</p>
          <p className="text-xs text-gray-500">
            {school.license_count ? `of ${school.license_count}` : ''}
          </p>
        </div>
      )
    },
    {
      key: 'teachers',
      label: 'Teachers',
      sortable: true,
      render: (school) => <span className="font-medium">{school.teacher_count || 0}</span>
    },
    {
      key: 'usage',
      label: 'License Usage',
      sortable: true,
      render: (school) => {
        const usage = school.license_count
          ? Math.round((school.student_count / school.license_count) * 100)
          : 0;
        const colorClass = usage >= 90 ? 'text-red-600' : usage >= 75 ? 'text-yellow-600' : 'text-green-600';
        return <span className={`font-semibold ${colorClass}`}>{usage}%</span>;
      }
    },
    {
      key: 'activation_code',
      label: 'Activation Code',
      render: (school) => (
        <button
          onClick={() => {
            navigator.clipboard.writeText(school.master_activation_code);
            alert(`Code copied: ${school.master_activation_code}`);
          }}
          className="text-purple-600 hover:text-purple-800 font-medium text-xs flex items-center"
          title={school.master_activation_code}
        >
          📋 Copy Code
        </button>
      )
    },
    {
      key: 'subscription_status',
      label: 'Status',
      sortable: true,
      render: (school) => {
        const status = school.subscription_status || 'unknown';
        const colorClass = status === 'active' ? 'text-green-600' : 'text-gray-500';
        return <span className={`font-medium capitalize ${colorClass}`}>{status}</span>;
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (school) => (
        <button
          onClick={() => window.location.href = `/super-admin/school/${school.id}`}
          className="text-indigo-600 hover:text-indigo-800 font-medium text-sm"
        >
          View Details →
        </button>
      )
    }
  ];

  if (loading) {
    return (
      <DashboardLayout
        title="Platform Admin Dashboard"
        navItems={superAdminNavItems}
        userRole={currentUser?.user_type || 'super-admin'}
        userName={currentUser?.email || 'Admin'}
      >
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600">Loading platform data...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout
        title="Platform Admin Dashboard"
        navItems={superAdminNavItems}
        userRole={currentUser?.user_type || 'super-admin'}
        userName={currentUser?.email || 'Admin'}
      >
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error loading dashboard: {error}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Platform Admin Dashboard"
      navItems={superAdminNavItems}
      userRole={currentUser?.user_type || 'super-admin'}
      userName={currentUser?.email || 'Admin'}
    >
      {/* Welcome Section */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900">
          Brillabee Platform Overview
        </h2>
        <p className="text-gray-600 mt-1">
          Manage all schools and monitor platform-wide metrics
        </p>
      </div>

      {/* Platform Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricsCard
          title="Total Schools"
          value={dashboardData?.platform_metrics?.total_schools || 0}
          subtitle={`${dashboardData?.platform_metrics?.active_schools || 0} active`}
          trend={dashboardData?.platform_metrics?.schools_growth_percent ? `▲ ${dashboardData.platform_metrics.schools_growth_percent}%` : ''}
          trendColor="green"
          icon="🏫"
        />
        <MetricsCard
          title="Total Students"
          value={(dashboardData?.platform_metrics?.total_students || 0).toLocaleString()}
          subtitle="across platform"
          trend={dashboardData?.platform_metrics?.students_growth_percent ? `▲ ${dashboardData.platform_metrics.students_growth_percent}%` : ''}
          trendColor="green"
          icon="👥"
        />
        <MetricsCard
          title="Total Teachers"
          value={(dashboardData?.platform_metrics?.total_teachers || 0).toLocaleString()}
          subtitle="on the platform"
          trend={dashboardData?.platform_metrics?.teachers_growth_percent ? `▲ ${dashboardData.platform_metrics.teachers_growth_percent}%` : ''}
          trendColor="green"
          icon="👨‍🏫"
        />
        <MetricsCard
          title="Total Sessions"
          value={(dashboardData?.platform_metrics?.total_sessions || 0).toLocaleString()}
          subtitle="all time"
          trend={dashboardData?.platform_metrics?.sessions_growth_percent ? `▲ ${dashboardData.platform_metrics.sessions_growth_percent}%` : ''}
          trendColor="green"
          icon="💬"
        />
      </div>

      {/* License Utilization */}
      {dashboardData?.platform_metrics?.license_utilization && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Platform License Utilization</h3>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Licenses</p>
              <p className="text-3xl font-bold text-gray-900">
                {(dashboardData.platform_metrics.license_utilization.total_licenses || 0).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Licenses Used</p>
              <p className="text-3xl font-bold text-indigo-600">
                {(dashboardData.platform_metrics.license_utilization.licenses_used || 0).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Utilization Rate</p>
              <p className="text-3xl font-bold text-green-600">
                {dashboardData.platform_metrics.license_utilization.utilization_percent || 0}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setShowCreateSchoolModal(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
          >
            + Create New School
          </button>
          <button
            onClick={() => window.location.href = '/super-admin/analytics'}
            className="px-4 py-2 bg-white text-purple-600 border-2 border-purple-600 rounded-lg hover:bg-purple-50 transition-colors font-medium"
          >
            📈 View Platform Analytics
          </button>
          <button
            onClick={() => window.location.href = '/super-admin/codes'}
            className="px-4 py-2 bg-white text-purple-600 border-2 border-purple-600 rounded-lg hover:bg-purple-50 transition-colors font-medium"
          >
            🔑 Manage Activation Codes
          </button>
        </div>
      </div>

      {/* Schools List */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-900">Schools</h3>
          <div className="flex items-center space-x-3">
            <input
              type="text"
              placeholder="Search schools..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
            />
            <span className="text-sm text-gray-600">
              {schools.length} school{schools.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
        <DataTable
          columns={schoolColumns}
          data={schools}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          emptyMessage="No schools found. Create your first school to get started!"
        />
      </div>

      {/* Create School Modal */}
      {showCreateSchoolModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 my-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Create New School
            </h3>

            {!createdSchool ? (
              <>
                <div className="space-y-4 mb-4 max-h-96 overflow-y-auto">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      School Name*
                    </label>
                    <input
                      type="text"
                      placeholder="Lincoln Elementary School"
                      value={schoolFormData.schoolName}
                      onChange={(e) => setSchoolFormData({...schoolFormData, schoolName: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      District Name
                    </label>
                    <input
                      type="text"
                      placeholder="New York City DOE"
                      value={schoolFormData.districtName}
                      onChange={(e) => setSchoolFormData({...schoolFormData, districtName: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                      </label>
                      <input
                        type="text"
                        placeholder="Brooklyn"
                        value={schoolFormData.city}
                        onChange={(e) => setSchoolFormData({...schoolFormData, city: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                      </label>
                      <input
                        type="text"
                        placeholder="NY"
                        value={schoolFormData.state}
                        onChange={(e) => setSchoolFormData({...schoolFormData, state: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        License Count*
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={schoolFormData.licenseCount}
                        onChange={(e) => setSchoolFormData({...schoolFormData, licenseCount: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Subscription (months)*
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={schoolFormData.subscriptionMonths}
                        onChange={(e) => setSchoolFormData({...schoolFormData, subscriptionMonths: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleCreateSchool}
                    disabled={createLoading}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    {createLoading ? 'Creating...' : 'Create School & Generate Code'}
                  </button>
                  <button
                    onClick={() => setShowCreateSchoolModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-green-600 font-semibold mb-3">
                  ✓ School created successfully!
                </p>
                <div className="bg-gray-50 rounded p-4 mb-4">
                  <p className="text-sm text-gray-600 mb-2">School:</p>
                  <p className="font-bold text-lg text-gray-900">{createdSchool.school?.school_name}</p>
                  <p className="text-sm text-gray-600 mt-3 mb-2">Master Activation Code:</p>
                  <p className="font-mono text-xl text-purple-600">{createdSchool.activation_code}</p>
                </div>
                <p className="text-sm text-gray-600 mb-1">
                  <strong>Licenses:</strong> {createdSchool.school?.license_count}
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  <strong>Expires:</strong> {createdSchool.expires_at ? new Date(createdSchool.expires_at).toLocaleDateString() : 'N/A'}
                </p>
                <p className="text-xs bg-blue-50 p-2 rounded mb-4 text-blue-800">
                  Share this master code with the school administrator to register their school.
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(createdSchool.activation_code);
                      alert('Master code copied to clipboard!');
                    }}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    📋 Copy Master Code
                  </button>
                  <button
                    onClick={() => {
                      setShowCreateSchoolModal(false);
                      setCreatedSchool(null);
                      setSchoolFormData({
                        schoolName: '',
                        districtName: '',
                        city: '',
                        state: '',
                        licenseCount: 100,
                        subscriptionMonths: 12
                      });
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default SuperAdminDashboard;
